// Stripe adapter — local cart state + Stripe Checkout via Workers API.
// Cart object shape is identical to Shopify cart so consumers need zero changes.

const WORKER = process.env.GATSBY_WORKER_DOMAIN || ''

function makeEmptyCart() {
  return {
    id: 'local_' + Date.now(),
    checkoutUrl: null,
    totalQuantity: 0,
    cost: {
      subtotalAmount: { amount: '0.00', currencyCode: 'USD' },
      totalAmount: { amount: '0.00', currencyCode: 'USD' },
    },
    lines: { nodes: [] },
  }
}

function recalcCart(cart) {
  const nodes = cart.lines.nodes
  const totalQty = nodes.reduce((s, l) => s + l.quantity, 0)
  const totalAmt = nodes
    .reduce((s, l) => s + parseFloat(l.cost.totalAmount.amount), 0)
    .toFixed(2)
  return {
    ...cart,
    totalQuantity: totalQty,
    cost: {
      subtotalAmount: { amount: totalAmt, currencyCode: 'USD' },
      totalAmount: { amount: totalAmt, currencyCode: 'USD' },
    },
  }
}

async function fetchVariant(variantId) {
  const resp = await fetch(`${WORKER}/variants/${variantId}`)
  if (!resp.ok) throw new Error(`Variant not found: ${variantId}`)
  return resp.json()
}

function buildCartLine(variant, quantity) {
  const unitPrice = parseFloat(variant.price) || 0
  return {
    id: 'line_' + variant.id,
    quantity,
    cost: {
      totalAmount: {
        amount: (unitPrice * quantity).toFixed(2),
        currencyCode: 'USD',
      },
    },
    merchandise: {
      id: variant.id,
      title: variant.title,
      price: variant.price, // non-Shopify extra field, useful for checkout
      selectedOptions: variant.selected_options || [],
      product: {
        id: variant.product_id || '',
        title: variant.product?.title || '',
        handle: variant.product?.handle || '',
        featuredImage: {
          id: null,
          url: variant.product?.images?.[0]?.url || '',
          altText: variant.product?.images?.[0]?.altText || '',
          width: 640,
          height: 640,
        },
      },
    },
  }
}

const stripeAdapter = {
  // No-op in Stripe mode (cart is local)
  makeClient: () => null,

  async getCart(_cartId) {
    // Local cart — nothing to fetch from server; caller should use localStorage
    return null
  },

  async createCart() {
    return makeEmptyCart()
  },

  async addLines(cart, items) {
    // items: [{ merchandiseId: variantId, quantity }]
    const variantDataList = await Promise.all(
      items.map((item) => fetchVariant(item.merchandiseId))
    )

    const newLines = variantDataList.map((variant, i) =>
      buildCartLine(variant, items[i].quantity || 1)
    )

    const existingNodes = cart?.lines?.nodes || []
    const merged = [...existingNodes]

    for (const newLine of newLines) {
      const existingIdx = merged.findIndex(
        (l) => l.merchandise.id === newLine.merchandise.id
      )
      if (existingIdx >= 0) {
        const existing = merged[existingIdx]
        const newQty = existing.quantity + newLine.quantity
        const unitPrice = parseFloat(existing.merchandise.price || '0')
        merged[existingIdx] = {
          ...existing,
          quantity: newQty,
          cost: {
            totalAmount: {
              amount: (unitPrice * newQty).toFixed(2),
              currencyCode: 'USD',
            },
          },
        }
      } else {
        merged.push(newLine)
      }
    }

    return recalcCart({ ...(cart || makeEmptyCart()), lines: { nodes: merged } })
  },

  async updateLine(cart, lineId, quantity) {
    const nodes = (cart?.lines?.nodes || []).map((line) => {
      if (line.id !== lineId) return line
      const unitPrice = parseFloat(line.merchandise.price || '0')
      return {
        ...line,
        quantity,
        cost: {
          totalAmount: {
            amount: (unitPrice * quantity).toFixed(2),
            currencyCode: 'USD',
          },
        },
      }
    })
    return recalcCart({ ...cart, lines: { nodes } })
  },

  async removeLine(cart, lineId) {
    const nodes = (cart?.lines?.nodes || []).filter((l) => l.id !== lineId)
    return recalcCart({ ...cart, lines: { nodes } })
  },

  async checkout(cart, customerEmail) {
    const lineItems = (cart?.lines?.nodes || []).map((line) => ({
      title: `${line.merchandise.product.title} - ${line.merchandise.title}`,
      price: line.merchandise.price || (parseFloat(line.cost.totalAmount.amount) / line.quantity).toFixed(2),
      quantity: line.quantity,
      image_url: line.merchandise.product.featuredImage?.url || undefined,
      variant_id: line.merchandise.id,
    }))

    const resp = await fetch(`${WORKER}/checkout/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line_items: lineItems,
        success_url: `${window.location.origin}/account?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: window.location.href,
        customer_email: customerEmail || undefined,
      }),
    })

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.message || 'Checkout failed')
    }

    const { url } = await resp.json()
    window.location.href = url
  },
}

export default stripeAdapter
